import React, { useState } from "react";
import { Icon } from "@iconify/react";
import { drawGiveawayWinner, stepGiveawayDraw, notifyGiveawayDraw } from "../services/giveawayService";
import {
  apiProblem,
  DEAD_ENDS,
  DRAW_MOVES,
  DRAW_STATUS_LABELS,
  DRAW_STEP_BUTTONS,
  fmtEt,
  NAVY,
  PURPLE,
  showProblem,
  toastOk,
} from "./giveawayUtils";
import { ActionModal, PlainList, SectionCard } from "./GiveawayUi";

// The random draw and what happens after it. A draw only picks a
// provisional winner: nothing is sent to them until "Send winner email" is
// pressed, and they only win after answering the skill-testing question and
// being confirmed eligible. Each person drawn gets a card with every step.

const SKILL_STEPS = ["skill_passed", "skill_failed"];

const drawPillStyle = (status) => {
  if (status === "awarded" || status === "confirmed")
    return { background: PURPLE, color: "#fff", border: `1px solid ${PURPLE}` };
  if (DEAD_ENDS.includes(status)) return { background: NAVY, color: "#fff", border: "1px solid rgba(140,80,253,0.45)" };
  return { background: "transparent", color: PURPLE, border: `1.5px solid ${PURPLE}` };
};

const GiveawayDrawTab = ({ g, reload, onUnauthorized }) => {
  const [modal, setModal] = useState(null);
  const [lastDraw, setLastDraw] = useState(null);
  const ds = g.drawState || {};
  const reasons = Array.isArray(ds.reasons) ? ds.reasons : [];
  const draws = Array.isArray(g.draws) ? [...g.draws].sort((a, b) => b.seq - a.seq) : [];

  const fail = (e, fallback, title) => {
    if (e?.response?.status === 401) {
      onUnauthorized();
      return true;
    }
    showProblem(apiProblem(e, fallback), title);
    return false;
  };

  const askDraw = () =>
    setModal({
      title: ds.isAlternate ? "Draw an alternate?" : "Select a random winner?",
      body: (
        <>
          <p className="mb-8">
            One entry is picked at random from all valid entries. Test entries are never included.
          </p>
          <p className="mb-0">
            This only selects a provisional winner. Nothing is sent to them. This cannot be undone, and
            the result is kept in the audit trail.
          </p>
        </>
      ),
      confirmLabel: ds.isAlternate ? "Draw an alternate" : "Select random winner",
      typeToConfirm: "DRAW",
      onConfirm: async () => {
        try {
          const res = await drawGiveawayWinner(g._id);
          setLastDraw(res.data?.data || null);
          await reload();
          return true;
        } catch (e) {
          return fail(e, "The draw did not happen.", "No draw");
        }
      },
    });

  const askStep = (d, to) => {
    const skill = SKILL_STEPS.includes(to);
    const fields = [];
    if (skill) {
      fields.push({
        name: "skillQuestion",
        label: "The question you asked",
        type: "textarea",
        required: true,
        initial: d.skillQuestion || "",
      });
      fields.push({ name: "skillAnswer", label: "The answer they gave", type: "text", required: true });
    }
    fields.push({
      name: "note",
      label: DEAD_ENDS.includes(to) ? "What happened?" : "Note (optional)",
      type: "textarea",
      required: DEAD_ENDS.includes(to),
    });
    setModal({
      title: DRAW_STEP_BUTTONS[to],
      body: DEAD_ENDS.includes(to)
        ? "This ends this person's chance. You can then draw an alternate."
        : to === "awarded"
        ? "Only press this once the prize has actually been handed over."
        : null,
      confirmLabel: "Save step",
      danger: DEAD_ENDS.includes(to),
      fields,
      onConfirm: async (v) => {
        try {
          const body = { to };
          if (v.note?.trim()) body.note = v.note.trim();
          if (skill) {
            body.skillQuestion = v.skillQuestion.trim();
            body.skillAnswer = v.skillAnswer.trim();
          }
          await stepGiveawayDraw(g._id, d.seq, body);
          toastOk("Step saved");
          await reload();
          return true;
        } catch (e) {
          return fail(e, "The step was not saved.");
        }
      },
    });
  };

  const askNotify = (d) =>
    setModal({
      title: "Email the selected person?",
      body: (
        <>
          <p className="mb-8">
            This emails the selected person. It does not tell them they have won; it asks them to confirm
            and explains the skill-testing question.
          </p>
          <p className="mb-0">
            Their secure link expires {d.contactDeadline ? `on ${fmtEt(d.contactDeadline)}` : "after the response window"}.
          </p>
        </>
      ),
      confirmLabel: "Send email",
      onConfirm: async () => {
        try {
          await notifyGiveawayDraw(g._id, d.seq);
          toastOk("Email sent");
          await reload();
          return true;
        } catch (e) {
          return fail(e, "The email was not sent.");
        }
      },
    });

  return (
    <div>
      <SectionCard
        title={ds.isAlternate ? "Draw an alternate" : "Random draw"}
        subtitle="Draws are made from valid entries only, after entries close."
      >
        <div className="d-flex align-items-start gap-3 flex-wrap">
          <button
            type="button"
            className="btn btn-primary fw-bold px-24"
            style={{ letterSpacing: ds.isAlternate ? 0 : "0.04em" }}
            disabled={!ds.canDraw}
            onClick={askDraw}
          >
            <Icon icon="mdi:dice-multiple-outline" className="me-2" />
            {ds.isAlternate ? "Draw an alternate" : "SELECT RANDOM WINNER"}
          </button>
          {!ds.canDraw && reasons.length ? (
            <div style={{ flex: "1 1 320px" }}>
              <div className="text-sm fw-semibold mb-4">Not possible yet, because:</div>
              <PlainList items={reasons} icon="mdi:close-circle-outline" />
            </div>
          ) : null}
        </div>

        {lastDraw ? (
          <div
            className="p-16 radius-8 mt-16"
            style={{ border: `1.5px solid ${PURPLE}`, background: "rgba(140, 80, 253, 0.08)" }}
          >
            <h6 className="fw-semibold mb-8">Provisional winner selected. Nothing has been sent to them.</h6>
            {lastDraw.entry ? (
              <div className="mb-8">
                <strong>
                  {lastDraw.entry.firstName} {lastDraw.entry.lastName}
                </strong>
                {lastDraw.entry.city ? `, ${lastDraw.entry.city}` : ""}
                <div className="text-sm">{lastDraw.entry.email}</div>
              </div>
            ) : null}
            <div className="text-sm">
              Draw number {lastDraw.seq}. Picked at random from {lastDraw.poolSize} valid entries.
            </div>
            <div className="text-xs text-secondary-light mt-1" style={{ wordBreak: "break-all" }}>
              Fingerprint of the entry list (proves which list was used): {lastDraw.poolHash}
            </div>
          </div>
        ) : null}
      </SectionCard>

      {draws.length === 0 ? (
        <div className="text-sm text-secondary-light">Nobody has been drawn yet.</div>
      ) : (
        draws.map((d) => {
          const nextMoves = DRAW_MOVES[d.status] || [];
          const canNotify = (d.status === "selected" || d.status === "contacted") && !d.noticeSentAt;
          return (
            <SectionCard
              key={d.seq}
              title={`Draw ${d.seq}${d.seq > 1 ? " (alternate)" : ""}`}
              subtitle={`Drawn ${fmtEt(d.drawnAt)} from ${d.poolSize} valid entries. Entry ID ${d.entryId}.`}
              actions={
                <span
                  style={{
                    ...drawPillStyle(d.status),
                    display: "inline-block",
                    padding: "3px 10px",
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {DRAW_STATUS_LABELS[d.status] || d.status}
                </span>
              }
            >
              <div className="row g-3 mb-16">
                <div className="col-md-4">
                  <div className="text-sm text-secondary-light">Must respond by</div>
                  <div className="fw-semibold">{fmtEt(d.contactDeadline, "No deadline set yet")}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-sm text-secondary-light">Winner email</div>
                  <div className="fw-semibold">{d.noticeSentAt ? `Sent ${fmtEt(d.noticeSentAt)}` : "Not sent"}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-sm text-secondary-light">Responded through their link</div>
                  <div className="fw-semibold">{d.respondedAt ? `Yes, ${fmtEt(d.respondedAt)}` : "Not yet"}</div>
                </div>
                {d.skillQuestion ? (
                  <div className="col-12">
                    <div className="text-sm text-secondary-light">Skill-testing question</div>
                    <div>
                      {d.skillQuestion}
                      {d.skillAnswerGiven ? (
                        <span className="text-secondary-light"> Answer given: {d.skillAnswerGiven}</span>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Steps timeline */}
              {Array.isArray(d.steps) && d.steps.length ? (
                <div className="mb-16">
                  <div className="text-sm fw-semibold mb-8">What has happened</div>
                  <ol className="ps-0 mb-0" style={{ listStyle: "none", borderLeft: `2px solid ${PURPLE}`, marginLeft: 6 }}>
                    {d.steps.map((s, i) => (
                      <li key={i} className="position-relative ps-16 pb-12">
                        <span
                          style={{
                            position: "absolute",
                            left: -7,
                            top: 4,
                            width: 12,
                            height: 12,
                            borderRadius: 999,
                            background: PURPLE,
                          }}
                        />
                        <div className="text-sm fw-semibold">{DRAW_STATUS_LABELS[s.to] || s.to}</div>
                        <div className="text-xs text-secondary-light">{fmtEt(s.at)}</div>
                        {s.note ? <div className="text-sm mt-1">{s.note}</div> : null}
                      </li>
                    ))}
                  </ol>
                </div>
              ) : null}

              <div className="d-flex flex-wrap gap-2">
                {canNotify ? (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={!g.emailsEnabled}
                    title={g.emailsEnabled ? "" : "Turn on Send giveaway emails in Settings first"}
                    onClick={() => askNotify(d)}
                  >
                    <Icon icon="mdi:email-fast-outline" className="me-1" />
                    Send winner email
                  </button>
                ) : null}
                {nextMoves.map((to) => (
                  <button
                    key={to}
                    type="button"
                    className={`btn btn-sm ${DEAD_ENDS.includes(to) ? "btn-outline-secondary" : "btn-outline-primary"}`}
                    onClick={() => askStep(d, to)}
                  >
                    {DRAW_STEP_BUTTONS[to]}
                  </button>
                ))}
              </div>
              {canNotify && !g.emailsEnabled ? (
                <div className="text-xs text-secondary-light mt-8">
                  Giveaway emails are off in Settings, so the winner email cannot be sent from here.
                </div>
              ) : null}
            </SectionCard>
          );
        })
      )}

      <ActionModal show={!!modal} {...(modal || {})} onClose={() => setModal(null)} />
    </div>
  );
};

export default GiveawayDrawTab;
